import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { AFTERPAY_SESSION_STORAGE_KEY } from './CustomCheckout';
import { useCart } from '../../context/CartContext';
import logger from '../../utils/logging';
import analyticsService from '../../services/analyticsService';

const AfterpayReturn = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { clearCart } = useCart();
  const [statusMessage, setStatusMessage] = useState({
    title: 'Zee is confirming your Afterpay sparkle...',
    body: 'Please hang tight while we capture your Afterpay approval and lock in your order.',
    isError: false,
    isCancelled: false
  });

  useEffect(() => {
    let isMounted = true;

    const handleReturn = async () => {
      const statusParam = (searchParams.get('status') || '').toLowerCase();
      const token = searchParams.get('orderToken') || searchParams.get('token');

      logger.log('info', 'checkout', 'Afterpay return detected', {
        statusParam,
        hasToken: !!token
      });

      if (statusParam === 'cancelled') {
        sessionStorage.removeItem(AFTERPAY_SESSION_STORAGE_KEY);
        if (isMounted) {
          setStatusMessage({
            title: 'Afterpay checkout cancelled',
            body: 'No worries—your Zee cart is waiting if you want to try again or switch to another payment option.',
            isError: false,
            isCancelled: true
          });
        }
        analyticsService.trackCheckoutStep('afterpay_cancelled', {});
        return;
      }

      if (!token) {
        if (isMounted) {
          setStatusMessage({
            title: 'Missing Afterpay token',
            body: 'We couldn’t find the Afterpay confirmation token. Please restart checkout so Zee can try again.',
            isError: true,
            isCancelled: false
          });
        }
        analyticsService.trackCheckoutStep('afterpay_missing_token', {});
        return;
      }

      const storedSessionRaw = sessionStorage.getItem(AFTERPAY_SESSION_STORAGE_KEY);

      if (!storedSessionRaw) {
        if (isMounted) {
          setStatusMessage({
            title: 'Afterpay session not found',
            body: 'Zee couldn’t locate your checkout session. Please begin checkout again so we can secure your order.',
            isError: true,
            isCancelled: false
          });
        }
        analyticsService.trackCheckoutStep('afterpay_session_missing', { token });
        return;
      }

      try {
        const storedSession = JSON.parse(storedSessionRaw);
        const backendUrl = process.env.REACT_APP_BACKEND_URL || 'https://api.rainbow-sails.shop:3443';

        setStatusMessage({
          title: 'Capturing your Afterpay approval...',
          body: 'Zee is locking in the payment and creating your order right now.',
          isError: false,
          isCancelled: false
        });

        const response = await fetch(`${backendUrl}/api/orders/afterpay/capture`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            token,
            order: storedSession.order,
            merchantReference: storedSession.merchantReference
          })
        });

        const data = await response.json();

        if (!response.ok || !data.success) {
          const message = data?.message || data?.error || `Afterpay capture failed (${response.status})`;
          throw new Error(message);
        }

        analyticsService.trackCheckoutStep('afterpay_capture', {
          token,
          orderNumber: data.orderNumber
        });

        sessionStorage.removeItem(AFTERPAY_SESSION_STORAGE_KEY);
        clearCart();

        if (isMounted) {
          setStatusMessage({
            title: 'Afterpay approved!',
            body: 'Redirecting you to Zee’s success portal...',
            isError: false,
            isCancelled: false
          });
        }

        navigate(`/checkout/success?order=${data.orderNumber}&printful=${data.printfulOrderId || ''}`, { replace: true });
      } catch (error) {
        console.error('❌ Afterpay capture error:', error);
        sessionStorage.removeItem(AFTERPAY_SESSION_STORAGE_KEY);
        if (isMounted) {
          setStatusMessage({
            title: 'Afterpay capture failed',
            body: error.message || 'Zee hit a snag while capturing your payment. Please try again or choose another method.',
            isError: true,
            isCancelled: false
          });
        }
        analyticsService.trackCheckoutStep('afterpay_capture_failed', {
          token,
          error: error.message
        });
      }
    };

    handleReturn();

    return () => {
      isMounted = false;
    };
  }, [searchParams, clearCart, navigate]);

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '2rem',
      background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e40af 100%)'
    }}>
      <div style={{
        maxWidth: '520px',
        width: '100%',
        padding: '2.5rem',
        borderRadius: '24px',
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.2)',
        backdropFilter: 'blur(18px)',
        textAlign: 'center',
        boxShadow: '0 24px 60px rgba(15, 23, 42, 0.45)'
      }}>
        <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>
          {statusMessage.isError ? '⚠️' : statusMessage.isCancelled ? '🌀' : '🌈'}
        </div>

        <h1 style={{
          fontSize: '2rem',
          marginBottom: '1rem',
          color: 'white',
          fontWeight: '700'
        }}>
          {statusMessage.title}
        </h1>

        <p style={{
          fontSize: '1rem',
          color: 'rgba(255, 255, 255, 0.78)',
          lineHeight: 1.6,
          marginBottom: statusMessage.isError || statusMessage.isCancelled ? '2rem' : 0
        }}>
          {statusMessage.body}
        </p>

        {(statusMessage.isError || statusMessage.isCancelled) && (
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <button
              onClick={() => {
                sessionStorage.removeItem(AFTERPAY_SESSION_STORAGE_KEY);
                navigate('/checkout');
              }}
              style={{
                padding: '0.75rem 1.5rem',
                borderRadius: '10px',
                border: 'none',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #ec4899 100%)',
                color: 'white',
                fontWeight: '600',
                cursor: 'pointer',
                boxShadow: '0 10px 25px rgba(139, 92, 246, 0.35)'
              }}
            >
              Return to Zee checkout
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AfterpayReturn;

